/**
 * Carry-state transition rules and the hand budget.
 *
 * The single source of truth for how many hands a weapon needs and whether a
 * carry-state transition is legal. Validation lives here (and is called from the
 * store actions) rather than in a render effect — see the armour reconciliation
 * post-mortem in `docs/data-schema-and-validation.md`.
 *
 * Action costs come from the Ready action, RT p.242 (`combat-actions.json`):
 * "Draw/Stow an Item/Weapon properly; Free Action if dropped".
 *
 * @see .kiro/specs/equipment-carry-state/design.md
 */

import { CyberneticsService } from './CyberneticsService';
import type { ActionCounters } from '../types/combatRound';
import { WeaponClass, type CarryState, type Equipment, type Weapon } from '../types/character';

/** Cost of a transition, in RT action economy terms. */
export type ActionCost = 'half' | 'free' | 'none';

/**
 * Which round-tracker counter a cost spends, or `null` when it spends nothing.
 *
 * Declared beside the costs so the two cannot drift. `none` covers stash and
 * retrieve: those are out-of-combat housekeeping, not actions in structured time.
 *
 * The tracker is advisory — see `CombatRoundBar`. Charging a cost records that the
 * action happened; it never gates the transition, and going over budget is a warning
 * for the player and GM to resolve, not a refusal.
 */
export const COUNTER_FOR_ACTION_COST: Readonly<Record<ActionCost, keyof ActionCounters | null>> = {
  half: 'halfActionsUsed',
  free: 'freeActionsUsed',
  none: null
};

/**
 * Why a transition was refused.
 *
 * `ok: false` is part of each member rather than intersected on afterwards: an
 * intersection over a union (`{ ok: false } & TransitionFailure`) is not a
 * discriminated union, so callers could not narrow with `if (result.ok) return`.
 */
export type TransitionFailure =
  | { ok: false; reason: 'hand-budget'; wielded: Weapon[]; needed: number; available: number }
  | { ok: false; reason: 'invalid-state'; from: CarryState; to: CarryState };

export type TransitionResult = { ok: true; actionCost: ActionCost } | TransitionFailure;

/** A refusal that the UI can resolve by stowing something. */
export type HandBudgetRefusal = Extract<TransitionFailure, { reason: 'hand-budget' }>;

/**
 * The refusal carried by a result, or `null` when the transition succeeded.
 *
 * The project compiles with `strict: false`, where TypeScript will narrow a union
 * *to* its truthy discriminant branch but not to the falsy one — so `if (result.ok)
 * return; result.reason` does not typecheck. The single assertion that expresses it
 * is confined here rather than repeated at every call site.
 */
export function refusalOf(result: TransitionResult): TransitionFailure | null {
  return result.ok ? null : (result as TransitionFailure);
}

/** Narrow a result to a hand-budget refusal — the one the UI can offer to fix. */
export function isHandBudgetRefusal(result: TransitionResult): result is HandBudgetRefusal {
  return refusalOf(result)?.reason === 'hand-budget';
}

/** A character has two physical hands. */
export const HAND_BUDGET = 2;

/**
 * Weapon classes that require both hands.
 *
 * No two-handed model existed before 2026-08-04; this is its single declaration.
 * Known simplification: RT has one-handed `basic` weapons (pistol-gripped) and
 * braced `heavy` weapons. Per-item overrides are deliberately out of scope — see
 * the spec's "Out of scope" section.
 */
export const TWO_HANDED_CLASSES: ReadonlySet<WeaponClass> = new Set([
  WeaponClass.Basic,
  WeaponClass.Heavy
]);

/**
 * Hands a weapon occupies when wielded.
 *
 * Returns 0 for hand-equivalents — a mechadendrite's built-in weapon, or a weapon
 * slaved to an installed MIU weapon interface. Those are wielded without hands, so
 * they must not consume the budget.
 */
export function handsRequired(weapon: Weapon, equipment: readonly Equipment[] = []): 0 | 1 | 2 {
  if (isHandEquivalent(weapon, equipment)) return 0;
  return TWO_HANDED_CLASSES.has(weapon.class) ? 2 : 1;
}

/** True when the weapon is wielded by an augmetic rather than a hand. */
export function isHandEquivalent(weapon: Weapon, equipment: readonly Equipment[] = []): boolean {
  // Mechadendrite built-in weapons are synthetic and body-mounted.
  if (weapon.isSynthetic) return true;
  const source = equipment.find((item) => item.sourceId === weapon.sourceId);
  if (source && CyberneticsService.isMechadendrite(source)) return true;
  // A weapon slaved to an installed MIU weapon interface needs no hand.
  return equipment.some((item) => item.sourceId === 'miu-weapon-interface' && item.installed);
}

/** Hands currently occupied by wielded weapons. */
export function handsInUse(
  weapons: readonly Weapon[],
  equipment: readonly Equipment[] = []
): number {
  return weapons
    .filter((w) => w.carryState === 'wielded')
    .reduce((total, w) => total + handsRequired(w, equipment), 0);
}

/**
 * Can this weapon be wielded right now?
 *
 * On refusal the currently-wielded weapons are returned so the caller can offer
 * "stow X and wield Y" as a single step rather than a dead end.
 */
export function canWield(
  weapon: Weapon,
  weapons: readonly Weapon[],
  equipment: readonly Equipment[] = []
): TransitionResult {
  const needed = handsRequired(weapon, equipment);
  // Exclude the weapon itself, so re-wielding an already-wielded weapon is a no-op
  // rather than a double count.
  const others = weapons.filter((w) => w.id !== weapon.id);
  const inUse = handsInUse(others, equipment);
  const available = HAND_BUDGET - inUse;

  if (needed > available) {
    return {
      ok: false,
      reason: 'hand-budget',
      wielded: others.filter((w) => w.carryState === 'wielded'),
      needed,
      available
    };
  }
  return { ok: true, actionCost: 'half' };
}

/** Legal transitions and their action costs. */
const TRANSITIONS: ReadonlyArray<{ from: CarryState; to: CarryState; cost: ActionCost }> = [
  { from: 'carried', to: 'wielded', cost: 'half' }, // draw — Ready
  { from: 'wielded', to: 'carried', cost: 'half' }, // stow — Ready
  { from: 'wielded', to: 'dropped', cost: 'free' }, // drop is a Free Action
  { from: 'dropped', to: 'wielded', cost: 'half' }, // recover — Ready
  { from: 'dropped', to: 'carried', cost: 'half' }, // recover and stow
  { from: 'carried', to: 'owned', cost: 'none' }, // stash — out of combat
  { from: 'owned', to: 'carried', cost: 'none' } // retrieve — out of combat
];

/** Is this a legal state change, and what does it cost? */
export function checkTransition(from: CarryState, to: CarryState): TransitionResult {
  const match = TRANSITIONS.find((t) => t.from === from && t.to === to);
  if (!match) return { ok: false, reason: 'invalid-state', from, to };
  return { ok: true, actionCost: match.cost };
}

/**
 * What a carry state means for the item that holds it, for UI labelling.
 *
 * Deliberately phrased from the character's point of view rather than as a bare
 * state name: "In hands" reads as a fact about the item, "wielded" reads as jargon.
 */
export const CARRY_STATE_LABELS: Readonly<Record<CarryState, string>> = {
  owned: 'Stashed',
  carried: 'Carried',
  wielded: 'In hands',
  dropped: 'On the ground'
};

/**
 * Which items a carry state applies to.
 *
 * General equipment is only ever stashed or carried — wielding and dropping are
 * weapon transitions, and the store exposes them as weapon actions only.
 */
export type CarryKind = 'weapon' | 'item';

export interface AvailableTransition {
  to: CarryState;
  /** Verb phrase for a menu entry, including the action cost. */
  label: string;
  cost: ActionCost;
}

/** Menu wording per transition. The action cost is appended by `availableTransitions`. */
const TRANSITION_VERBS: Readonly<Record<string, string>> = {
  'carried>wielded': 'Wield',
  'wielded>carried': 'Stow',
  'wielded>dropped': 'Drop',
  'dropped>wielded': 'Pick up and wield',
  'dropped>carried': 'Pick up and stow',
  'carried>owned': 'Stash',
  'owned>carried': 'Retrieve'
};

const COST_SUFFIX: Readonly<Record<ActionCost, string>> = {
  half: ' (Half Action)',
  free: ' (Free Action)',
  none: '' // out of combat, so no action economy to report
};

/** Weapon-only targets, filtered out for general equipment. */
const WEAPON_ONLY_STATES: ReadonlySet<CarryState> = new Set<CarryState>(['wielded', 'dropped']);

/**
 * Legal transitions out of a state, labelled for a menu.
 *
 * Derived from the same `TRANSITIONS` table `checkTransition` uses, so the menu
 * cannot offer an option the store would refuse as `invalid-state`. Hand-budget
 * refusals are *not* filtered here: a wield that would exceed two hands is still
 * offered, because the refusal carries the wielded list and the UI turns that into
 * "stow X and wield Y" rather than a disabled option with no explanation.
 */
export function availableTransitions(
  from: CarryState,
  kind: CarryKind = 'weapon'
): AvailableTransition[] {
  return TRANSITIONS.filter((t) => t.from === from)
    .filter((t) => kind === 'weapon' || !WEAPON_ONLY_STATES.has(t.to))
    .map((t) => ({
      to: t.to,
      label: `${TRANSITION_VERBS[`${t.from}>${t.to}`] ?? t.to}${COST_SUFFIX[t.cost]}`,
      cost: t.cost
    }));
}

/** Human-readable refusal, for UI messages. */
export function describeFailure(failure: TransitionFailure): string {
  if (failure.reason === 'invalid-state') {
    return `Cannot go from ${failure.from} to ${failure.to}.`;
  }
  const names = failure.wielded.map((w) => w.customName ?? w.sourceId).join(', ');
  const hands = failure.needed === 1 ? 'hand' : 'hands';
  return `Needs ${failure.needed} ${hands}, but only ${failure.available} free. Currently wielding: ${names || 'nothing'}.`;
}
